import { useRef, useEffect, useMemo, useState } from 'react';
import { Input } from '../../../../../components/ui/input';
import { Button } from '../../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { cn } from '../../../../../lib/utils';
import { FilterValueInput } from './FilterValueInput';
import { FkLookupValueButton } from './FkLookupValueButton';
import { JsonPathField } from './JsonPathField';
import { EnumValuePicker } from './EnumValuePicker';
import { useJsonEnumPickerSlot } from './useJsonEnumPickerSlot';
import { openDeclareEnumColumnDialog } from './declareEnumColumnDialogStore';
import { findWithSchemaFold } from '../../utils/metaInfoSchemaMatch';
import {
  JSON_SUB_OPERATORS,
  adjustPathForSubOp,
  defaultSubOpForType,
  getJsonSubOperatorLabel,
  isJsonpathMethodsSupported,
  jsonSubOperatorIsDocumentLevel,
  jsonSubOperatorIsLookup,
  jsonSubOperatorIsNumeric,
  jsonSubOperatorRequiresPath,
  jsonSubOperatorRequiresValue,
  validSubOpsForType,
  type JsonPathTypeHint,
} from '../../utils/filterUtils';
import { usePgServerMajorVersion } from '../../hooks/usePgServerMajorVersion';
import {
  findLooseRelationshipsForColumn,
  resolveActiveLooseRel,
} from '../../utils/looseFkResolver';
import {
  defaultSubOpForEnumDeclaration,
  findEnumDeclarationForPath,
} from '../../utils/pathEnumLookup';
import type { FilterCondition, FilterGroup, JsonSubOperator } from '../../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { DatabaseMetaInfo } from '../../../../../types/electron';
import { JSON_PATH_HINT_TEXT, getJsonPathHint } from './jsonFilterFieldsValidator';
import {
  isMssqlOpenJsonBlocked,
  useMssqlCompatLevel,
  useMssqlCompatRecheck,
} from '../../hooks/useMssqlCompatLevel';

/**
 * Sub-operators split into semantic groups for the dropdown. Keeps the
 * list scannable: presence-on-path, comparison-on-path, aggregate
 * (any/all over wildcard `[*]` paths), then document-level checks.
 */
const JSON_SUB_OPERATOR_GROUPS: ReadonlyArray<{
  label: string;
  ops: ReadonlyArray<JsonSubOperator>;
}> = [
  { label: 'Presence', ops: ['has_property', 'not_has_property'] },
  {
    label: 'Comparison',
    ops: [
      'path_eq',
      // Case-insensitive scalar equality (PG19+ only; gated below).
      'path_ieq',
      'path_neq',
      'path_is_null',
      'path_is_not_null',
      'path_contains',
      'path_gt',
      'path_gte',
      'path_lt',
      'path_lte',
      // Pass 3 Slice C: scalar-equality lookup against a related table
      // (value comes from the FK row picker rather than free text).
      'property_lookup',
      // JSON-path enum: value comes from the EnumValuePicker (declared
      // enum on a scalar JSONPath) rather than free text.
      'property_enum',
    ],
  },
  {
    // "Aggregate" was internal jargon — users filtering JSON arrays look
    // for a way to say "at least one element matches X". Surface that
    // intent in the group label so they spot it without reading the docs.
    label: 'Array elements (any / all)',
    ops: [
      'any_eq',
      // Case-insensitive element equality (PG19+ only; gated below).
      'any_ieq',
      'any_contains',
      'any_gt',
      'any_gte',
      'any_lt',
      'any_lte',
      'any_lookup',
      'any_enum',
      'all_eq',
      'all_gt',
      'all_gte',
      'all_lt',
      'all_lte',
      'all_lookup',
      'all_enum',
    ],
  },
  { label: 'Document', ops: ['is_empty', 'is_not_empty', 'is_valid', 'is_not_valid'] },
];

/**
 * Sub-operators that rely on PG19 jsonpath string methods (`.lower()`). These
 * are hidden from the dropdown unless the connection is pgsql ≥ 19
 * (`isJsonpathMethodsSupported`) — PG ≤ 18 raises a query-time jsonpath syntax
 * error, so there's no graceful degradation to a disabled state at emit time.
 */
const JSONPATH_METHOD_SUB_OPS = new Set<JsonSubOperator>(['path_ieq', 'any_ieq']);

export type JsonPeekContext = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
  engine: DatabaseEngineId;
  declaredOnText: boolean;
  /**
   * Source column data type — surfaced so the `*_lookup` sub-ops (Pass 3
   * Slice C) can route a first-time declaration through the loose-FK
   * target picker with the same inference context the chip's `lookup`
   * operator uses.
   */
  dataType?: string;
  /**
   * MetaInfo for the current `(connection, database)` — needed by the
   * `*_lookup` sub-ops to resolve which loose-FK declaration drives the
   * picker for a given `(column, condition.path)` pair.
   */
  metaInfo?: DatabaseMetaInfo | null;
  /**
   * Applied Table Explorer filters (minus this chip's own group) to scope
   * the Peek popover's scan to — filter-aware JSON peek (Path A). Optional:
   * omitted on every host that doesn't yet thread it, preserving today's
   * whole-column peek behavior.
   */
  scanFilters?: FilterGroup[];
};

type JsonFilterFieldsProps = {
  condition: FilterCondition;
  onChange: (updates: Partial<FilterCondition>) => void;
  /** Auto-focus the path input on mount (used in DraftFilterChip). */
  autoFocusPath?: boolean;
  /** Disable inputs while a confirmation modal is pending. */
  disabled?: boolean;
  /**
   * Engine used to make the path validator engine-aware (Slice 13:
   * recursive-descent `..` is supported on PG/Oracle, flagged elsewhere).
   * Defaults to `peekContext?.engine` when omitted, so callers that
   * already pass peekContext don't need to set this.
   */
  engine?: DatabaseEngineId;
  /**
   * When provided, render the Peek button next to the path input. Omit
   * (e.g. when missing connection/database) to hide the button.
   */
  peekContext?: JsonPeekContext;
  /**
   * Apply button ref from the parent draft chip. After a Peek pick, focus
   * lands here when the current sub-op has no value input — so Enter can
   * commit the filter without reaching for the mouse.
   */
  applyButtonRef?: React.RefObject<HTMLButtonElement | null>;
};

/**
 * Inline field cluster for the `json` filter operator: sub-operator
 * dropdown + JSONPath input + (optional) comparand value input.
 *
 * The component is layout-only — it does not own validation, declaration
 * confirmation, or commit semantics. Parent chips drive those.
 */
export function JsonFilterFields({
  condition,
  onChange,
  autoFocusPath,
  disabled,
  engine,
  peekContext,
  applyButtonRef,
}: JsonFilterFieldsProps) {
  const validatorEngine = engine ?? peekContext?.engine;
  const sub: JsonSubOperator = condition.jsonSubOperator ?? 'has_property';
  const valueInputRef = useRef<HTMLInputElement>(null);
  // Fallback path input ref used only when `peekContext` is absent —
  // without a connection/database we can't open the Peek popover, so the
  // chip falls back to a plain `<Input>` and `autoFocusPath` targets it.
  const fallbackPathRef = useRef<HTMLInputElement>(null);

  // MSSQL compat level — drives the wildcard `[*]` path hint and the
  // OPENJSON warning banner in the operator dropdown when the database
  // is below compat 130. No-op (returns `null`) on every other engine.
  const { data: mssqlCompatLevel } = useMssqlCompatLevel(
    peekContext?.connectionId ?? null,
    peekContext?.database ?? null,
    validatorEngine,
  );
  // Banner only matters once the user has a wildcard path — without `[*]`
  // the scalar emit uses `JSON_VALUE` (no OPENJSON) and the warning would
  // be noise. `is_empty` would also fail on low compat, but it's a rare
  // first-pick — the runtime error rewrite catches that case.
  const openJsonBlocked =
    isMssqlOpenJsonBlocked(validatorEngine, mssqlCompatLevel) &&
    (condition.path ?? '').includes('[*]');
  // Recheck — lets the user pick up an externally-bumped compat level
  // without waiting for the 1 h TanStack Query staleTime.
  const { recheck: handleRecheckCompat, isFetching: mssqlCompatFetching } =
    useMssqlCompatRecheck(peekContext?.connectionId ?? null, peekContext?.database ?? null);

  // PG19 jsonpath string methods (`.lower()`) gate the case-insensitive
  // `path_ieq` / `any_ieq` sub-ops. Resolve the server major version the same
  // way the mssql-compat check resolves compat level (keyed on connection +
  // database); unknown ⇒ not supported (PG rejects the jsonpath at query time,
  // so an optimistic guess would hard-error PG ≤ 18 users).
  const pgMajorVersion = usePgServerMajorVersion(
    peekContext?.connectionId ?? null,
    peekContext?.database ?? null,
    validatorEngine,
  );
  const jsonpathMethodsSupported = validatorEngine
    ? isJsonpathMethodsSupported(validatorEngine, pgMajorVersion)
    : false;

  const showPath = jsonSubOperatorRequiresPath(sub);
  const showValue = jsonSubOperatorRequiresValue(sub);
  const numericValue = jsonSubOperatorIsNumeric(sub);
  const isLookup = jsonSubOperatorIsLookup(sub);
  const isEnumSub = sub === 'property_enum' || sub === 'any_enum' || sub === 'all_enum';
  const isDocumentLevel = jsonSubOperatorIsDocumentLevel(sub);
  const declaredOnText = peekContext?.declaredOnText ?? false;
  const pathValue = condition.path ?? '';
  const pathHint = useMemo(
    () =>
      showPath
        ? getJsonPathHint(pathValue, {
            engine: validatorEngine,
            mssqlCompatLevel,
          })
        : null,
    [showPath, pathValue, validatorEngine, mssqlCompatLevel],
  );
  const pathMalformed = pathHint !== null;

  // Resolve the observed type for the current path from the
  // `(connection, database)` structure cache — used to narrow the sub-op
  // dropdown to type-relevant options. Falls back to `undefined` when
  // the path hasn't been observed (e.g. user typed it freehand against
  // a freshly-declared column) and the chip keeps the broad list.
  const observedPathType = useMemo<JsonPathTypeHint | undefined>(() => {
    if (!peekContext?.metaInfo || !pathValue) return undefined;
    const entry = findWithSchemaFold(
      peekContext.metaInfo.jsonStructureCache,
      peekContext.schema,
      (e) => e.table === peekContext.table && e.column === peekContext.column,
    );
    if (!entry) return undefined;
    return entry.paths.find((p) => p.path === pathValue)?.type;
  }, [peekContext?.metaInfo, peekContext?.schema, peekContext?.table, peekContext?.column, pathValue]);

  const allowedSubOps = useMemo(() => {
    // Whole-column mode: narrow the dropdown to the three document-level
    // ops. Without this the user sees `any =`, `all <` etc. in a state
    // where they don't apply — the path is empty, so those operators
    // would emit nonsense SQL or be silently ignored. "Show all
    // operators…" still escapes the narrow set for the rare case
    // someone wants to pivot back to path mode through the dropdown
    // alone, without re-opening Peek.
    if (isDocumentLevel) {
      return new Set<JsonSubOperator>([
        'is_valid',
        'is_not_valid',
        'is_empty',
        'is_not_empty',
      ]);
    }
    // Path mode: start from the type-narrowed set, but always include the
    // document-level ops (`is_valid`, `is_not_valid`, `is_empty`,
    // `is_not_empty`). They apply to the column as a whole and are a useful
    // escape from path mode — picking one auto-clears the path via
    // `handleSubOpChange`.
    const base = validSubOpsForType(observedPathType, pathValue);
    const merged = new Set<JsonSubOperator>(base);
    merged.add('is_valid');
    merged.add('is_not_valid');
    merged.add('is_empty');
    merged.add('is_not_empty');
    return merged;
  }, [isDocumentLevel, observedPathType, pathValue]);

  // Whether the type-filtered list is a strict subset of the full one.
  // Drives whether to render the "Show all operators…" footer at all —
  // when the list is already full (unknown path type, or no peek context),
  // the footer would be a no-op.
  const isSubOpListPruned = allowedSubOps.size < JSON_SUB_OPERATORS.length;

  // Per-open expander state. Closing the dropdown resets it so the next
  // open returns to the narrow, type-filtered list (the recommended
  // default for the chip — power-user expansion is a single-session opt-in).
  const [showAllSubOps, setShowAllSubOps] = useState(false);
  const [subOpDropdownOpen, setSubOpDropdownOpen] = useState(false);
  useEffect(() => {
    if (!subOpDropdownOpen) setShowAllSubOps(false);
  }, [subOpDropdownOpen]);
  const renderingFullList = !isSubOpListPruned || showAllSubOps;

  // One-line hint surfaced at the top of the dropdown when the list is
  // pruned to a type-relevant subset. Tells the user *why* the list is
  // shorter so the trimmed set doesn't feel arbitrary. Skipped when the
  // user has clicked "Show all operators…" (renderingFullList) or when
  // there's nothing to prune in the first place.
  const narrowingHint = useMemo<string | null>(() => {
    if (!isSubOpListPruned || showAllSubOps) return null;
    if (isDocumentLevel) return 'Operators relevant for whole document';
    switch (observedPathType) {
      case 'string': return 'Operators relevant for strings';
      case 'number': return 'Operators relevant for numbers';
      case 'boolean': return 'Operators relevant for booleans';
      case 'object': return 'Operators relevant for objects';
      case 'array': return 'Operators relevant for arrays';
      case 'null': return 'Operators relevant for null values';
      case 'mixed': return 'Operators relevant for mixed values';
      default: return null;
    }
  }, [isSubOpListPruned, showAllSubOps, isDocumentLevel, observedPathType]);

  // Handler for sub-op changes — also rewrites the path's terminal `[*]`
  // so the path field visibly mirrors what's actually queried. Mid-path
  // wildcards are left untouched (`adjustPathForSubOp` only touches the
  // terminal segment).
  const handleSubOpChange = (next: JsonSubOperator) => {
    const updates: Partial<FilterCondition> = { jsonSubOperator: next };
    if (jsonSubOperatorIsDocumentLevel(next)) {
      // Document-level ops don't carry a path — clear it so the chip's
      // collapsed label reads "<col> is valid JSON" rather than carrying
      // a stale path the engine ignores.
      updates.path = '';
    } else if (pathValue) {
      const adjusted = adjustPathForSubOp(pathValue, next);
      if (adjusted !== pathValue) updates.path = adjusted;
    }
    onChange(updates);
  };

  // Tree-pick handler — when the user clicks a row in the path tree we
  // also auto-pick the most useful sub-op for that observed type, AND
  // adjust the path for the new sub-op (so an array pick lands on `[*]`).
  //
  // If a MetaInfo enum declaration covers the exact picked path, prefer
  // the enum-aware sub-op (`property_enum` for scalar paths,
  // `any_enum` for array paths or any path that walks `[*]`). That way
  // the wizard immediately exposes the EnumValuePicker the user already
  // declared, rather than dropping them on a generic `has_property`.
  const handleTreePick = (
    pickedPath: string,
    type: JsonPathTypeHint | undefined,
  ) => {
    const decl = peekContext?.metaInfo
      ? findEnumDeclarationForPath(
          peekContext.metaInfo.enumColumns,
          peekContext.schema,
          peekContext.table,
          peekContext.column,
          pickedPath,
        )
      : undefined;
    const nextSub: JsonSubOperator = decl
      ? defaultSubOpForEnumDeclaration(decl, pickedPath)
      : defaultSubOpForType(type, pickedPath);
    const adjustedPath = adjustPathForSubOp(pickedPath, nextSub);
    onChange({ jsonSubOperator: nextSub, path: adjustedPath });
  };

  // Whole-column footer-link pick — switches the chip into document-level
  // mode by stamping the engine-appropriate default sub-op and clearing
  // the path. The picker stays visible (its label flips to "whole
  // column"), so the user can round-trip back to path mode just by
  // clicking the picker and picking a tree row.
  const handleDocumentScopePick = (
    nextSub: 'is_valid' | 'is_not_valid' | 'is_empty' | 'is_not_empty',
  ) => {
    // The popover's footer link calls this with the legacy engine-default
    // sentinel (`is_valid` declared-on-text, `is_empty` native). UX
    // decision: both sentinels collapse to `is_not_empty` — the most
    // useful "did the user actually populate this JSON" predicate.
    // Any other explicit value the popover passes (`is_not_valid`,
    // `is_not_empty`) is honored — those are user-selected ops.
    const sub =
      nextSub === 'is_valid' || nextSub === 'is_empty' ? 'is_not_empty' : nextSub;
    onChange({ jsonSubOperator: sub, path: '' });
  };

  // Picker-`×` handler — clearing the path puts the chip into
  // whole-column mode, so seed the sub-op to the most useful
  // document-level default (`is_not_empty`) rather than a stale
  // path-mode op. That keeps the chip immediately meaningful ("is not
  // empty object/array") instead of demanding the user re-pick a
  // sub-op before the filter does anything.
  const handlePickerClear = () => {
    onChange({ jsonSubOperator: 'is_not_empty', path: '' });
  };

  // For `*_lookup` sub-ops (Pass 3 Slice C), resolve the active loose-FK
  // by `(source column, condition.path)`. Empty result = first-time UX
  // (the picker falls through to LooseRelationshipTargetPicker); a match
  // = subsequent UX (row-grid view opens directly).
  const activeLooseRel = useMemo(() => {
    if (!isLookup || !peekContext?.metaInfo) return null;
    const rels = findLooseRelationshipsForColumn(
      peekContext.metaInfo.looseRelationships,
      peekContext.schema,
      peekContext.table,
      peekContext.column,
    );
    return resolveActiveLooseRel(rels, pathValue || undefined);
  }, [
    isLookup,
    peekContext?.metaInfo,
    peekContext?.schema,
    peekContext?.table,
    peekContext?.column,
    pathValue,
  ]);

  // JSON-path enum slot — resolved unconditionally (rules-of-hooks). The
  // hook returns `{ available: false }` when any of schema/table/column/
  // path is missing, so an unconditional call is safe even for non-enum
  // sub-ops or when peekContext is absent. Mirrors how `activeLooseRel`
  // drives the `*_lookup` picker, but keyed on enum-column declarations
  // (`(schema, table, column, path)`) rather than loose-FK rows.
  const jsonEnumSlot = useJsonEnumPickerSlot({
    metaInfo: peekContext?.metaInfo,
    schema: peekContext?.schema,
    table: peekContext?.table,
    column: peekContext?.column,
    path: pathValue || undefined,
  });

  useEffect(() => {
    if (autoFocusPath && !peekContext && fallbackPathRef.current) {
      const t = setTimeout(() => {
        fallbackPathRef.current?.focus();
        fallbackPathRef.current?.select();
      }, 50);
      return () => clearTimeout(t);
    }
  }, [autoFocusPath, peekContext]);

  // After picking a path from Peek, move focus forward so the keyboard
  // flow stays uninterrupted: into the value input when the sub-op has
  // one, otherwise onto the parent's Apply button.
  const handlePickPathComplete = () => {
    if (showValue && valueInputRef.current) {
      valueInputRef.current.focus();
      valueInputRef.current.select();
      return;
    }
    applyButtonRef?.current?.focus();
  };

  return (
    <>
      {(showPath || isDocumentLevel) && peekContext && (
        <JsonPathField
          connectionId={peekContext.connectionId}
          database={peekContext.database}
          schema={peekContext.schema}
          table={peekContext.table}
          column={peekContext.column}
          engine={peekContext.engine}
          declaredOnText={peekContext.declaredOnText}
          scanFilters={peekContext.scanFilters}
          disabled={disabled}
          value={pathValue}
          autoFocusPath={autoFocusPath}
          onChange={(path) => onChange({ path })}
          onTreePick={handleTreePick}
          onPickDocumentScope={handleDocumentScopePick}
          onClear={handlePickerClear}
          onPickPathComplete={handlePickPathComplete}
          scope={isDocumentLevel ? 'whole-column' : 'path'}
          // property_lookup binds a single scalar JSON property — the
          // wildcard tip is misleading there. any_lookup / all_lookup
          // walk arrays and benefit from the tip (the default).
          showWildcardHint={sub === 'property_lookup' ? false : undefined}
        />
      )}

      {showPath && !peekContext && (
        <span className="inline-flex flex-col">
          <Input
            ref={fallbackPathRef}
            type="text"
            value={pathValue}
            onChange={(e) => onChange({ path: e.target.value })}
            placeholder="e.g. $.path…"
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            translate="no"
            aria-label="JSON path"
            aria-invalid={pathMalformed || undefined}
            disabled={disabled}
            className={cn(
              'h-6 w-56 px-2 text-xs font-mono',
              pathMalformed && 'border-destructive/60 focus-visible:ring-destructive/40',
            )}
            data-testid="json-path-input"
          />
          {pathHint && (
            <span
              className="mt-0.5 text-[10px] text-destructive pointer-events-none"
              data-testid="json-path-malformed-hint"
            >
              {JSON_PATH_HINT_TEXT[pathHint]}
            </span>
          )}
        </span>
      )}

      <Select
        value={sub}
        onValueChange={(v: JsonSubOperator) => handleSubOpChange(v)}
        open={subOpDropdownOpen}
        onOpenChange={setSubOpDropdownOpen}
        disabled={disabled}
      >
        <SelectTrigger
          className="h-6 w-auto min-w-[110px] px-2 text-xs"
          data-testid="json-sub-operator"
          aria-label="JSON sub-operator"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent
          // Zero the Radix Viewport's `p-1` padding so the warning
          // banner (top) and the sticky "Show all operators…" footer
          // (bottom) span the full popover width with no transparent
          // strips around them. SelectItems / SelectLabels carry their
          // own padding (`py-1.5 pl-2`, `px-2 py-1.5`) so they still
          // look right against the popover edges.
          viewportClassName=""
          portalled={false}
        >
          {narrowingHint && (
            <div
              className="px-2 py-1 text-[10px] text-muted-foreground"
              data-testid="json-sub-operator-narrowing-hint"
            >
              {narrowingHint}
            </div>
          )}
          {openJsonBlocked && (
            <div
              className="px-2 py-1 text-[10px] text-destructive border-b border-border"
              data-testid="json-sub-operator-openjson-hint"
            >
              <div>Array filters need compat 130+.</div>
              <div className="mt-0.5 text-right">
                <button
                  type="button"
                  disabled={mssqlCompatFetching}
                  onPointerDown={(e) => {
                    // Radix Select swallows clicks inside SelectContent; stopping
                    // pointerdown keeps the dropdown open and lets the button
                    // handle its own click.
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void handleRecheckCompat();
                  }}
                  className="underline hover:no-underline disabled:opacity-50"
                  data-testid="json-sub-operator-recheck-compat"
                >
                  {mssqlCompatFetching ? 'Checking…' : 'Recheck'}
                </button>
              </div>
            </div>
          )}
          {JSON_SUB_OPERATOR_GROUPS.map((group) => {
            // Always keep the *currently selected* op visible in its
            // native group even if it falls outside the filtered set —
            // otherwise Radix Select shows a populated trigger but the
            // open dropdown wouldn't highlight the active choice.
            const ops = group.ops.filter((op) => {
              // PG19-only case-insensitive jsonpath ops: hide entirely unless
              // the server supports them (pgsql ≥ 19). Keep the currently
              // selected op visible so a restored filter still highlights its
              // choice even on an unsupported server. This runs before the
              // `renderingFullList` escape so "Show all operators…" can't
              // surface them on PG ≤ 18 either.
              if (
                JSONPATH_METHOD_SUB_OPS.has(op) &&
                !jsonpathMethodsSupported &&
                op !== sub
              ) {
                return false;
              }
              return renderingFullList || allowedSubOps.has(op) || op === sub;
            });
            if (ops.length === 0) return null;
            return (
              <SelectGroup key={group.label}>
                <SelectLabel className="px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {group.label}
                </SelectLabel>
                {ops.map((op) => (
                  <SelectItem
                    key={op}
                    value={op}
                    className="text-xs"
                    title={
                      op === 'is_valid' || op === 'is_not_valid'
                        ? 'On native JSON columns this collapses to a NULL-check (validity is guaranteed by storage).'
                        : undefined
                    }
                  >
                    {getJsonSubOperatorLabel(op, declaredOnText)}
                  </SelectItem>
                ))}
              </SelectGroup>
            );
          })}
          {isSubOpListPruned && !showAllSubOps && (
            <div
              // Viewport padding is zeroed at the SelectContent level,
              // so the sticky footer naturally spans full width and
              // sticks flush to the popover bottom — no negative
              // margins or pseudo-strips needed.
              className="sticky bottom-0 py-1 bg-popover border-t border-border"
            >
              <button
                type="button"
                // Radix Select closes on pointer-down inside any
                // descendant by default — prevent that so the menu stays
                // open while we just toggle the visible set.
                onPointerDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAllSubOps(true);
                }}
                className="w-full text-left px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="json-sub-operator-show-all"
              >
                Show all operators…
              </button>
            </div>
          )}
        </SelectContent>
      </Select>

      {showValue && isEnumSub && peekContext && jsonEnumSlot.available && !jsonEnumSlot.declared && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="json-enum-declare"
          disabled={!pathValue || disabled}
          onClick={() =>
            openDeclareEnumColumnDialog({
              connectionId: peekContext.connectionId,
              database: peekContext.database,
              schema: peekContext.schema,
              table: peekContext.table,
              column: peekContext.column,
              engine: peekContext.engine,
              mode: 'eligible',
              trigger: 'filter-chip',
              path: pathValue,
              pathKind: sub === 'property_enum' ? 'scalar' : 'array',
            })
          }
        >
          Declare “{pathValue}” as enum
        </Button>
      )}

      {showValue && isEnumSub && peekContext && jsonEnumSlot.available && jsonEnumSlot.declared && (
        <EnumValuePicker
          connectionId={peekContext.connectionId}
          database={peekContext.database}
          schema={peekContext.schema}
          table={peekContext.table}
          column={peekContext.column}
          engine={peekContext.engine}
          path={pathValue || undefined}
          pathKind={jsonEnumSlot.pathKind}
          operator={sub === 'property_enum' ? 'eq' : 'in'}
          value={
            condition.value as
              | string
              | number
              | boolean
              | Date
              | null
              | undefined
              | string[]
          }
          onChange={(v) => onChange({ value: v })}
          ariaLabel="JSON enum value"
        />
      )}

      {showValue && isLookup && peekContext && (
        <FkLookupValueButton
          connectionId={peekContext.connectionId}
          database={peekContext.database}
          sourceColumn={peekContext.column}
          sourceSchema={peekContext.schema}
          sourceTable={peekContext.table}
          sourceColumnDataType={peekContext.dataType ?? 'unknown'}
          referencedSchema={activeLooseRel?.toSchema ?? ''}
          referencedTable={activeLooseRel?.toTable ?? ''}
          referencedColumn={activeLooseRel?.toColumn ?? ''}
          value={condition.value as string | number | boolean | Date | null | undefined}
          engine={peekContext.engine}
          sourcePath={pathValue || undefined}
          onChange={(v, resolvedSourcePath) => {
            // The picker resolved which loose-FK declaration the user
            // picked against. For *_lookup sub-ops the JSON path IS the
            // loose-FK key, so mirror it onto condition.sourcePath so
            // downstream consumers (FilterChip's collapsed label, future
            // surfaces) see the binding. Falls back to the user's typed
            // path for the first-time-declaration case where the picker's
            // own resolution would still be empty pre-save.
            const sourcePath =
              resolvedSourcePath ?? (pathValue || undefined);
            onChange({ value: v, sourcePath });
          }}
        />
      )}

      {showValue && !(isLookup && peekContext) && !(isEnumSub && peekContext) && (
        <FilterValueInput
          ref={valueInputRef}
          value={(condition.value ?? '') as string | number | null | undefined}
          onChange={(v) => onChange({ value: v })}
          dataTypeCategory={numericValue ? 'number' : 'string'}
          disabled={disabled}
          aria-label="JSON value"
          className="w-40"
        />
      )}
    </>
  );
}
